import { ErrorOutline } from "@mui/icons-material";
import { styled, Typography } from "@mui/material";
import { ReactElement } from "react";

import { Subaccount } from "~frontend/__generated__/graphql";
import { palette } from "~frontend/theme";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import { plural } from "~frontend/util/plural";

import { FlexBox } from "../FlexBox";
import { ExternalLink } from "../Link";
import Loading from "../Loading";

const ERROR_MESSAGE_DISPLAY_TIME_MS = 30 * 1000; // import errors don't display after 30s

const NoBulletList = styled(FlexBox)({
  paddingLeft: "20px",
  flexDirection: "column",
});

const ErrorRow = styled(FlexBox)({
  justifyContent: "flex-start",
  flexDirection: "row",
});

const RowNumber = ({
  children,
  variant,
}: {
  children: ReactElement | string | number;
  variant?: "body2" | "body2Bold";
}) => {
  return (
    <Typography variant={variant ?? "body2"} sx={{ width: "32px" }}>
      {children}
    </Typography>
  );
};

const RowError = ({
  children,
  variant,
}: {
  children: ReactElement | string | number;
  variant?: "body2" | "body2Bold";
}) => {
  return <Typography variant={variant ?? "body2"}>{children}</Typography>;
};
const ErrorContainer = styled(FlexBox)({
  width: "100%",
  borderColor: palette.red,
  borderRadius: "4px",
  borderWidth: "1px",
  borderStyle: "solid",
  padding: "10px 16px",
});

export function ResponseScriptExternalImportSummary({
  job,
}: {
  job: Subaccount["responseScriptImportToSubaccountJob"];
}): ReactElement {
  if (!job) {
    return <div />;
  }

  if (jobInProgress(job)) {
    return (
      <FlexBox
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%" }}
      >
        <Loading message="Importing Response Scripts..." />
      </FlexBox>
    );
  }

  const { failed, errorMessage } = jobFailed(job);

  if (failed) {
    return Date.now() - job.createdAt < ERROR_MESSAGE_DISPLAY_TIME_MS ? (
      <ErrorContainer alignItems="flex-start" gap={1}>
        <ErrorOutline color="error" sx={{ height: "16px", width: "16px" }} />
        <Typography variant="body2" color={palette.red}>
          <strong>Error: </strong>
          {errorMessage}
        </Typography>
      </ErrorContainer>
    ) : (
      <></>
    );
  }

  if (job.result && job.result.errors && job.result.errors.length > 0) {
    return (
      <ErrorContainer flexDirection="column">
        <FlexBox
          alignItems="flex-start"
          gap="1"
          flexDirection="row"
          justifyContent="space-between"
        >
          <FlexBox flexDirection="row" gap={1}>
            <ErrorOutline
              color="error"
              sx={{ height: "16px", width: "16px" }}
            />
            <Typography variant="body2Bold" color={palette.red}>
              Error importing response scripts
            </Typography>
          </FlexBox>
          <Typography variant="body3">
            <ExternalLink href={job.args!.sheetUrl}>Import Source</ExternalLink>
          </Typography>
        </FlexBox>

        <NoBulletList>
          <ErrorRow>
            <RowNumber variant="body2Bold">Row</RowNumber>
            <RowError variant="body2Bold">Error</RowError>
          </ErrorRow>

          {job.result.errors.slice(0, 10).map(err => (
            <ErrorRow>
              <RowNumber>{err.row}</RowNumber>
              <RowError>{err.msg}</RowError>
            </ErrorRow>
          ))}
          {job.result.errors.length > 10 ? (
            <Typography variant="body2">
              ...and {job.result.errors.length - 10} more
            </Typography>
          ) : null}
        </NoBulletList>
      </ErrorContainer>
    );
  }
  return (
    <FlexBox
      justifyContent="space-between"
      flexDirection="row"
      sx={{
        width: "100%",
        backgroundColor: palette.lightestGreen,
        px: 2,
        py: 1,
      }}
    >
      <FlexBox flexDirection="column">
        <Typography variant="body2Bold" fontSize={12}>
          Import Successful
        </Typography>
        <NoBulletList>
          <Typography
            key="import-summary-rows"
            component={"li"}
            variant="body2"
          >
            {plural(job.result?.nRows || 0, "Row", "Rows")} Processed
          </Typography>
          <Typography
            key="import-summary-scripts"
            component={"li"}
            variant="body2"
          >
            {plural(job.result?.nScriptsImported || 0, "Script", "Scripts")}{" "}
            Imported
          </Typography>
          <Typography
            key="import-summary-groups"
            component={"li"}
            variant="body2"
          >
            {plural(job.result?.nGroupsCreated || 0, "Group", "Groups")} Created
          </Typography>
        </NoBulletList>
      </FlexBox>
      <Typography variant="body3">
        <ExternalLink href={job.args.sheetUrl}>Import Source</ExternalLink>
      </Typography>
    </FlexBox>
  );
}
